import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../app/hooks'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../app/store'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  GET_OPD_DIAGNOSIS_ENDPOINT,
  POST_OPD_DIAGNOSIS_ENDPOINT,
} from '../../../utils/Endpoints'
import {
  opdFormDataSaveApi,
  opdFormDataFetchApi,
} from '../../../services/OpdFormsSTTService'
import {
  getVisitType,
  FORMIK_VALIDATIONS,
  toastMessages,
  DIAGNOSIS_TEXT_FIELDS,
  ASTERISK_CONDITION,
} from './constants'
import {
  selectDiagnosis,
  isInitialLoadForOPDForms,
  setLoading,
  updateDiagnosis,
  isDiagnosisOpdFormSubmitted,
  setIsDiagnosisOpdFormSubmit,
} from './opdFormsSlice'

const styles = (theme: Theme) => ({
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      minWidth: '250px',
      padding: '4px',
    },
  },
  fieldLabel: {
    textAlign: 'right',
    fontSize: '12px',
    textWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      minWidth: '150px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '161px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '220px',
    },
  },
  requried: {
    color: 'red',
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '72%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '65%',
    },
  },
})

const Diagnosis = (intentType: any) => {
  const visitType = getVisitType(intentType.intent)
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const diagnosisData: any = useAppSelector(selectDiagnosis)
  const dispatch = useDispatch<AppDispatch>()
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isDiagnosisOpdFormSubmitted)

  const initialValues = {
    provisionalDiagnosis: diagnosisData?.provisionalDiagnosis?.toString() || '',
    differentialDiagnosis:
      diagnosisData?.differentialDiagnosis?.toString() || '',
    investigations: diagnosisData?.investigations?.toString() || '',
    finalDiagnosis: diagnosisData?.finalDiagnosis?.toString() || '',
  }

  const validationSchema = (visitType: string) => {
    return Yup?.object({
      provisionalDiagnosis: Yup?.string()?.required(
        FORMIK_VALIDATIONS.ProvisionalDiagnosis
      ),
      differentialDiagnosis: Yup?.string(),
      investigations: Yup?.string(),
      finalDiagnosis:
        visitType === 'physio'
          ? Yup?.string()?.required(FORMIK_VALIDATIONS.FinalDiagnosis)
          : Yup?.string(),
    })
  }

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema(visitType),
    onSubmit: (values, { setSubmitting }) => {
      handleDiagnosisSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleDiagnosisSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_DIAGNOSIS_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsDiagnosisOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getDiagnosis = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getDiagnosisUrl = `${GET_OPD_DIAGNOSIS_ENDPOINT}${id}&visitType=opd/${visitType}`
    const response = await opdFormDataFetchApi(getDiagnosisUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateDiagnosis({
          provisionalDiagnosis: result?.provDiag,
          differentialDiagnosis: result?.diffDiag,
          investigations: result?.investDiag,
          finalDiagnosis: result?.finalDiag,
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }

    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getDiagnosis()
  }, [getDiagnosis])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={1}
      width={'fit-content'}
      className={classes?.customFontBox}
    >
      {DIAGNOSIS_TEXT_FIELDS?.filter((row) =>
        row?.validVisitTypes?.includes(visitType)
      )?.map(({ name, label }) => {
        const showAsterisk =
          name === ASTERISK_CONDITION.provisionalDiagnosis ||
          (name === ASTERISK_CONDITION.finalDiagnosis &&
            visitType === ASTERISK_CONDITION.physioVisitType)

        return (
          <Box
            display={'flex'}
            justifyContent={'start'}
            alignItems={'center'}
            width={'100%'}
            key={name}
            flexWrap={'wrap'}
            gap={1}
          >
            <Typography className={classes?.fieldLabel}>
              {label}
              {showAsterisk && <span className={classes?.requried}>*</span>}:
            </Typography>

            <TextField
              inputProps={{ 'data-testid': name }}
              multiline
              rows={3}
              variant='outlined'
              name={name}
              value={formik?.values[name as keyof typeof formik.values] || ''}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched[name as keyof typeof formik.values] &&
                Boolean(formik?.errors[name as keyof typeof formik.errors])
              }
              className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
            />
          </Box>
        )
      })}

      <Box
        display={'flex'}
        justifyContent={'flex-end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default Diagnosis
